import { Link, useNavigate, useParams } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import {
  businessRoleWords,
  SUPPORT_LIMITS,
  fillSavedReply,
  supportPriorities,
  supportPriorityWords,
  supportStateWords,
  supportTicketLabel,
  supportUrgencyWords,
  type AdminSupportCard,
  type AdminSupportMessage,
  type AdminSupportStaff,
  type AdminSupportStatus,
  type AdminSupportTag,
  type AdminSupportTicket,
  type SupportUrgency,
} from "@teamna/shared";
import { useMemo, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import {
  Badge,
  Button,
  Card,
  ErrorState,
  InlineError,
  PageHeader,
  PaperclipIcon,
  SegmentedControl,
  Select,
  SelectField,
  SkeletonList,
  TextAreaField,
  Toolbar,
  showToast,
  type BadgeTone,
} from "@teamna/ui/components/index.ts";
import { copyForError } from "@teamna/web-client/api/copy.ts";
import { formatCountdown, formatDateTime } from "@teamna/web-client/lib/format.ts";
import { setActingAs } from "../lib/act-as.ts";
import {
  useSavedReplies,
  useStaffMe,
  useSupportBoard,
  useSupportMove,
  useSupportReply,
  useSupportTicketForStaff,
  useSupportTicketTags,
  useUpdateSupportTicket,
} from "../lib/cc-api.ts";
import {
  AssigneeBadge,
  ColorDot,
  NO_FILTERS,
  PRIORITY_TONES,
  SUPPORT_COLORS,
  SupportTabs,
  TagChips,
  TeamFilterSelects,
  filtering,
  liveFilters,
  matchesFilters,
  staffName,
  useStoredFilters,
} from "./support-parts.tsx";

/**
 * Support tickets in Control Center (strategy repo: teamna/strategy/support-system.md, section 6).
 *
 *   /support             the board: one column per status, cards in rank order. Drag a card to
 *                        another column or under another card; every card also has "Move to", so
 *                        the board works with a keyboard. Filters and a find field above it.
 *   /support/list        every ticket in a table, with search and filters (support-list.tsx).
 *   /support/settings    the columns, the labels and the saved replies (support-settings.tsx).
 *   /support/$ticketId   one ticket: the thread with the team notes, a reply or a note (with a
 *                        saved reply and "And move to"), the column, the team member, the
 *                        priority, the labels, the sender, what the app added, the business and
 *                        "Open as this business".
 *
 * The column names are for the Teamna team. The sender reads the words of the kind, shown here
 * under each column name so the team knows what the customer sees.
 */

const URGENCY_TONES: Record<SupportUrgency, BadgeTone> = { low: "quiet", normal: "quiet", blocking: "cancelled" };
const ROLE_WORDS = businessRoleWords;

// --- the board ----------------------------------------------------------------------------

/** The words of a card the find field reads: the number, the subject and the business name. */
function findsCard(card: AdminSupportCard, find: string): boolean {
  if (!find) return true;
  const text = find.toLowerCase().replace(/^#/, "");
  return String(card.number).includes(text) || card.subject.toLowerCase().includes(text) || card.business.name.toLowerCase().includes(text);
}

export function SupportBoardScreen() {
  const board = useSupportBoard();
  const me = useStaffMe();
  const move = useSupportMove();
  const [dragging, setDragging] = useState<string | null>(null);
  const [storedFilters, setFilters] = useStoredFilters("board");
  const [find, setFind] = useState("");
  const myEmail = me.data?.email ?? null;
  const filters = useMemo(
    () => liveFilters(storedFilters, board.data?.tags ?? [], board.data?.staff ?? []),
    [storedFilters, board.data],
  );

  const columns = useMemo(() => {
    const data = board.data;
    if (!data) return [];
    const shown = data.tickets.filter((ticket) => matchesFilters(ticket, filters, myEmail) && findsCard(ticket, find.trim()));
    return data.statuses.map((status) => ({
      status,
      cards: shown.filter((ticket) => ticket.statusId === status.id).sort((a, b) => a.boardRank - b.boardRank),
    }));
  }, [board.data, filters, myEmail, find]);
  const narrowed = filtering(filters) || find.trim() !== "";

  const moveTo = (ticketId: string, statusId: string, afterTicketId: string | null) => {
    if (ticketId === afterTicketId) return;
    const expectedKind = board.data?.statuses.find((status) => status.id === statusId)?.kind;
    if (!expectedKind) return;
    move.mutate(
      { ticketId, statusId, afterTicketId, expectedKind },
      { onError: (error) => showToast(copyForError(error).message, { tone: "info" }) },
    );
  };

  let body: ReactNode;
  if (board.isLoading && !board.data) {
    body = (
      <output aria-label="Loading the board" className="block">
        <SkeletonList rows={3} />
      </output>
    );
  } else if (!board.data) {
    body = <ErrorState error={board.error} onRetry={() => void board.refetch()} />;
  } else {
    body = (
      <>
        <Toolbar
          search={{ value: find, onChange: setFind, placeholder: "Find a ticket on the board" }}
          actions={
            narrowed ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setFilters(NO_FILTERS);
                  setFind("");
                }}
              >
                Clear the filters
              </Button>
            ) : null
          }
        >
          <TeamFilterSelects filters={filters} onChange={setFilters} tags={board.data.tags} staff={board.data.staff} />
        </Toolbar>
      <ul className="-mx-1 flex gap-3 overflow-x-auto px-1 pb-4" aria-label="Support board">
        {columns.map(({ status, cards }) => (
          <Column
            key={status.id}
            status={status}
            cards={cards}
            statuses={board.data!.statuses}
            staff={board.data!.staff}
            dragging={dragging}
            onDragStart={setDragging}
            onDragEnd={() => setDragging(null)}
            onDrop={(afterTicketId) => {
              if (dragging) moveTo(dragging, status.id, afterTicketId);
              setDragging(null);
            }}
            onMove={moveTo}
          />
        ))}
      </ul>
      </>
    );
  }

  const open = board.data?.tickets.filter((ticket) => ticket.kind !== "closed").length ?? 0;
  const waiting = board.data?.tickets.filter((ticket) => ticket.waitingOnTeam && ticket.kind !== "closed").length ?? 0;
  return (
    <>
      <PageHeader
        title="Support"
        description={board.data ? `${open} open, ${waiting} waiting for an answer from Teamna. Resolved tickets stay here for 14 days.` : undefined}
      />
      <SupportTabs current="board" />
      {body}
    </>
  );
}

function Column({
  status,
  cards,
  statuses,
  staff,
  dragging,
  onDragStart,
  onDragEnd,
  onDrop,
  onMove,
}: {
  status: AdminSupportStatus;
  cards: AdminSupportCard[];
  statuses: AdminSupportStatus[];
  staff: AdminSupportStaff[];
  dragging: string | null;
  onDragStart: (ticketId: string) => void;
  onDragEnd: () => void;
  /** Dropped under this card, or at the top of the column for null. */
  onDrop: (afterTicketId: string | null) => void;
  onMove: (ticketId: string, statusId: string, afterTicketId: string | null) => void;
}) {
  const [over, setOver] = useState(false);
  const allowDrop = (event: DragEvent) => {
    if (!dragging) return;
    event.preventDefault();
    setOver(true);
  };
  return (
    <li aria-label={`${status.name}, ${cards.length} ${cards.length === 1 ? "ticket" : "tickets"}`} className="flex w-[272px] shrink-0">
      {/* The drop zone of the whole column: a drop on no card puts the card at the top. */}
      <div
        onDragOver={allowDrop}
        onDragLeave={() => setOver(false)}
        onDrop={(event) => {
          event.preventDefault();
          setOver(false);
          onDrop(null);
        }}
        className={["flex w-full flex-col gap-2 rounded-card border bg-surface p-2", over ? "border-accent" : "border-line"].join(" ")}
      >
        <header className="rounded-xl bg-paper px-3 pt-2.5 pb-2" style={{ borderTop: `3px solid ${SUPPORT_COLORS[status.color]}` }}>
          <p className="flex items-baseline justify-between gap-2">
            <span className="text-[14px] font-semibold text-ink">{status.name}</span>
            <span className="text-[13px] text-ink-2 tabular-nums">{cards.length}</span>
          </p>
          <p className="mt-0.5 text-[12px] text-ink-2">The sender reads: {supportStateWords[status.kind]}</p>
        </header>
        <ol className="flex flex-col gap-2" aria-label={`Tickets in ${status.name}`}>
          {cards.map((card) => (
            <li key={card.id}>
              {/* A drop on a card puts the dragged card right under it. */}
              <div
                onDragOver={allowDrop}
                onDrop={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setOver(false);
                  onDrop(card.id);
                }}
              >
                <TicketCard card={card} statuses={statuses} staff={staff} onDragStart={onDragStart} onDragEnd={onDragEnd} onMove={onMove} />
              </div>
            </li>
          ))}
        </ol>
      </div>
    </li>
  );
}

function TicketCard({
  card,
  statuses,
  staff,
  onDragStart,
  onDragEnd,
  onMove,
}: {
  card: AdminSupportCard;
  statuses: AdminSupportStatus[];
  staff: AdminSupportStaff[];
  onDragStart: (ticketId: string) => void;
  onDragEnd: () => void;
  onMove: (ticketId: string, statusId: string, afterTicketId: string | null) => void;
}) {
  return (
    <div
      draggable
      onDragStart={(event) => {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", card.id);
        onDragStart(card.id);
      }}
      onDragEnd={onDragEnd}
      className="cursor-grab rounded-xl border border-line bg-paper p-3 shadow-card active:cursor-grabbing"
    >
      <Link to="/support/$ticketId" params={{ ticketId: card.id }} className="block">
        <p className="flex items-center justify-between gap-2 text-[12px] text-ink-2">
          <span className="tabular-nums">{supportTicketLabel(card.number)}</span>
          <span>{formatCountdown(card.lastActivityAt)}</span>
        </p>
        <p dir="auto" className="mt-1 text-left text-[14px] leading-snug font-semibold text-ink">
          {card.subject}
        </p>
        <p className="mt-1 text-[12px] text-ink-2">
          <span dir="auto">{card.business.name}</span> · {ROLE_WORDS[card.senderRole]}
          {card.business.demo ? " · Demo" : ""}
        </p>
      </Link>
      <div className="mt-2 flex flex-wrap items-center gap-1.5">
        {card.urgency !== "normal" ? <Badge tone={URGENCY_TONES[card.urgency]}>{supportUrgencyWords[card.urgency]}</Badge> : null}
        {card.priority !== "none" ? <Badge tone={PRIORITY_TONES[card.priority]}>{supportPriorityWords[card.priority]}</Badge> : null}
        {card.waitingOnTeam && card.kind !== "closed" ? <Badge tone="late">Customer wrote</Badge> : null}
        {card.assigneeStaffEmail ? (
          <span className="ml-auto">
            <AssigneeBadge name={staffName(staff, card.assigneeStaffEmail)} />
          </span>
        ) : null}
      </div>
      {card.tags.length > 0 ? (
        <div className="mt-2">
          <TagChips tags={card.tags} />
        </div>
      ) : null}
      <div className="mt-2">
        <Select
          label={`Move ${supportTicketLabel(card.number)} to`}
          value={card.statusId}
          onChange={(event) => onMove(card.id, event.target.value, null)}
        >
          {statuses.map((status) => (
            <option key={status.id} value={status.id}>
              {status.id === card.statusId ? `In ${status.name}` : `Move to ${status.name}`}
            </option>
          ))}
        </Select>
      </div>
    </div>
  );
}

// --- one ticket ---------------------------------------------------------------------------

export function SupportTicketScreen() {
  const { ticketId } = useParams({ strict: false }) as { ticketId: string };
  const ticket = useSupportTicketForStaff(ticketId);
  const board = useSupportBoard();

  if (!ticket.data) {
    return (
      <>
        <PageHeader title="Ticket" back={{ to: "/support", label: "Support" }} />
        {ticket.isLoading ? (
          <output aria-label="Loading the ticket" className="block">
            <SkeletonList rows={4} />
          </output>
        ) : (
          <ErrorState error={ticket.error} onRetry={() => void ticket.refetch()} />
        )}
      </>
    );
  }

  const data = ticket.data;
  const statuses = board.data?.statuses ?? [];
  return (
    <>
      <PageHeader
        eyebrow={`${supportTicketLabel(data.number)} · ${data.business.name}`}
        title={data.subject}
        back={{ to: "/support", label: "Support" }}
      />
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="flex min-w-0 flex-col gap-5">
          <Thread messages={data.messages} />
          <Composer ticket={data} statuses={statuses} />
        </div>
        <aside className="flex flex-col gap-4" aria-label="About this ticket">
          <TicketFacts ticket={data} statuses={statuses} tags={board.data?.tags ?? []} staff={board.data?.staff ?? []} />
          <BusinessCard ticket={data} />
        </aside>
      </div>
    </>
  );
}

function Thread({ messages }: { messages: AdminSupportMessage[] }) {
  return (
    <ol className="flex flex-col gap-3" aria-label="Messages">
      {messages.map((message) => (
        <li key={message.id}>
          <MessageItem message={message} />
        </li>
      ))}
    </ol>
  );
}

function MessageItem({ message }: { message: AdminSupportMessage }) {
  if (message.author === "system") {
    return (
      <p className="text-center text-[13px] text-ink-2">
        <span dir="auto">{message.body}</span> · {formatDateTime(message.createdAt)}
        {message.internal ? " · Only Teamna sees this" : " · The sender sees this"}
      </p>
    );
  }
  const team = message.author === "team";
  return (
    <div className={["flex flex-col", team ? "items-end" : "items-start"].join(" ")}>
      <span className="mb-1 px-1 text-[12px] font-semibold text-ink-2">
        <span dir="auto">{message.authorName ?? (team ? "Teamna" : "Customer")}</span>
        {team ? " (Teamna)" : ""} · {formatDateTime(message.createdAt)}
      </span>
      {message.internal ? (
        <div className="notice notice-warning max-w-[85%] rounded-card px-3.5 py-2.5">
          <p className="text-[12px] font-semibold">Note. Only Teamna sees this.</p>
          <p dir="auto" className="mt-1 text-[15px] leading-[1.45] whitespace-pre-wrap break-words">
            {message.body}
          </p>
        </div>
      ) : (
        <div
          dir="auto"
          className={[
            "bubble max-w-[85%] rounded-card px-3.5 py-2.5 text-[15px] leading-[1.45] whitespace-pre-wrap break-words",
            team ? "bubble-mine rounded-br-md" : "bubble-theirs rounded-bl-md",
          ].join(" ")}
        >
          {message.body}
        </div>
      )}
      {message.files.length > 0 ? (
        <ul className="mt-1.5 flex flex-wrap gap-2" aria-label="Files">
          {message.files.map((file) => (
            <li key={file.id}>
              {file.url && file.contentType.startsWith("image/") ? (
                <a href={file.url} target="_blank" rel="noreferrer noopener" className="block overflow-hidden rounded-xl border border-line">
                  <img src={file.url} alt={file.fileName} className="h-32 w-auto max-w-[260px] object-cover" loading="lazy" />
                </a>
              ) : (
                <a
                  href={file.url ?? undefined}
                  target="_blank"
                  rel="noreferrer noopener"
                  className="flex items-center gap-1.5 rounded-pill border border-line bg-paper px-3 py-1.5 text-[13px] font-semibold text-accent-strong"
                >
                  <PaperclipIcon size={14} />
                  <span dir="auto">{file.fileName}</span>
                </a>
              )}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function Composer({ ticket, statuses }: { ticket: AdminSupportTicket; statuses: AdminSupportStatus[] }) {
  const reply = useSupportReply(ticket.id);
  const saved = useSavedReplies();
  const [mode, setMode] = useState<"reply" | "note">("reply");
  const [body, setBody] = useState("");
  const [moveTo, setMoveTo] = useState("");

  /** A saved reply goes into the composer with its fields filled; the team member edits it before send. */
  function insertSaved(replyId: string) {
    const picked = saved.data?.find((item) => item.id === replyId);
    if (!picked) return;
    const text = fillSavedReply(picked.body, { senderName: ticket.sender.name, businessName: ticket.business.name });
    setBody((current) => (current.trim() ? `${current.trimEnd()}\n\n${text}` : text).slice(0, SUPPORT_LIMITS.body));
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!body.trim()) return;
    reply.mutate(
      {
        body: body.trim(),
        internal: mode === "note",
        ...(moveTo ? { moveToStatusId: moveTo, moveToKind: statuses.find((status) => status.id === moveTo)?.kind } : {}),
      },
      {
        onSuccess: () => {
          setBody("");
          setMoveTo("");
          showToast(mode === "note" ? "Note saved." : "Reply sent.");
        },
      },
    );
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3 border-t border-line pt-4" noValidate>
      {reply.error ? <InlineError error={reply.error} /> : null}
      <SegmentedControl
        label="Reply or note"
        value={mode}
        onChange={setMode}
        options={[
          { value: "reply", label: "Reply to the sender" },
          { value: "note", label: "Note for Teamna" },
        ]}
      />
      {saved.data && saved.data.length > 0 ? (
        <div className="sm:w-80">
          <Select label="Put in a saved reply" value="" onChange={(event) => insertSaved(event.target.value)}>
            <option value="">Put in a saved reply</option>
            {saved.data.map((item) => (
              <option key={item.id} value={item.id}>
                {item.title}
              </option>
            ))}
          </Select>
        </div>
      ) : null}
      <TextAreaField
        label={mode === "reply" ? "Reply" : "Note"}
        maxLength={SUPPORT_LIMITS.body}
        hint={mode === "reply" ? "The sender reads this in the app." : "Only the Teamna team sees this."}
        rows={4}
        value={body}
        onChange={(event) => setBody(event.target.value)}
        dir="auto"
      />
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        <div className="sm:w-64">
          <SelectField label="And move to" size="compact" value={moveTo} onChange={(event) => setMoveTo(event.target.value)}>
            <option value="">Do not move</option>
            {statuses
              .filter((status) => status.id !== ticket.statusId)
              .map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
          </SelectField>
        </div>
        <Button type="submit" loading={reply.isPending} disabled={!body.trim()}>
          {mode === "reply" ? "Send reply" : "Save note"}
        </Button>
      </div>
    </form>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5 py-2">
      <dt className="text-[12px] font-semibold text-ink-2">{label}</dt>
      <dd className="text-[14px] break-words text-ink">{children}</dd>
    </div>
  );
}

const CONTEXT_WORDS: Record<string, string> = {
  platform: "Platform",
  appVersion: "App version",
  page: "Page before Help",
  device: "Browser or device",
  role: "Role",
  timezone: "Time zone",
};

/**
 * The labels of a ticket: every label is a toggle button, pressed when the ticket has it. A press
 * saves at once. The labels themselves are made in Labels and columns.
 */
function TicketTags({ ticket, tags }: { ticket: AdminSupportTicket; tags: AdminSupportTag[] }) {
  const save = useSupportTicketTags(ticket.id);
  const on = new Set(ticket.tags.map((tag) => tag.id));
  if (tags.length === 0) {
    return (
      <span className="text-ink-2">
        No labels yet.{" "}
        <Link to="/support/settings" className="font-semibold text-accent-strong">
          Add labels
        </Link>
      </span>
    );
  }
  const toggle = (tagId: string) => {
    const next = on.has(tagId) ? [...on].filter((id) => id !== tagId) : [...on, tagId];
    save.mutate(next, { onError: (error) => showToast(copyForError(error).message, { tone: "info" }) });
  };
  return (
    <ul className="mt-1 flex flex-wrap gap-1.5" aria-label="Labels of this ticket">
      {tags.map((tag) => {
        const pressed = on.has(tag.id);
        return (
          <li key={tag.id}>
            <button
              type="button"
              aria-pressed={pressed}
              disabled={save.isPending}
              onClick={() => toggle(tag.id)}
              className={[
                "inline-flex min-h-8 items-center gap-1.5 rounded-pill border px-2.5 text-[13px] font-semibold transition-colors disabled:opacity-60",
                "focus-visible:outline-3 focus-visible:outline-offset-2 focus-visible:outline-accent-soft",
                pressed ? "border-ink bg-sunken text-ink" : "border-line bg-paper text-ink-2 hover:border-line-strong",
              ].join(" ")}
            >
              <ColorDot color={tag.color} size={8} />
              <span dir="auto">{tag.name}</span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function TicketFacts({
  ticket,
  statuses,
  tags,
  staff,
}: {
  ticket: AdminSupportTicket;
  statuses: AdminSupportStatus[];
  tags: AdminSupportTag[];
  staff: AdminSupportStaff[];
}) {
  const move = useSupportMove();
  const update = useUpdateSupportTicket(ticket.id);
  const onError = (error: Error) => showToast(copyForError(error).message, { tone: "info" });
  // A team member who never signed in again still shows on the tickets they had.
  const members =
    ticket.assigneeStaffEmail && !staff.some((member) => member.email === ticket.assigneeStaffEmail)
      ? [...staff, { email: ticket.assigneeStaffEmail, name: null }]
      : staff;
  return (
    <Card className="px-4 py-2">
      <dl className="divide-y divide-line">
        <Fact label="Column">
          {statuses.length > 0 ? (
            <Select
              label="Column"
              value={ticket.statusId}
              onChange={(event) => {
                const target = statuses.find((status) => status.id === event.target.value);
                if (!target) return;
                move.mutate(
                  { ticketId: ticket.id, statusId: target.id, afterTicketId: null, expectedKind: target.kind },
                  { onError: (error) => showToast(copyForError(error).message, { tone: "info" }) },
                );
              }}
            >
              {statuses.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </Select>
          ) : null}
          <span className="mt-1 block text-[12px] text-ink-2">The sender reads: {supportStateWords[ticket.kind]}</span>
        </Fact>
        <Fact label="Team member">
          <Select
            label="Team member"
            value={ticket.assigneeStaffEmail ?? ""}
            disabled={update.isPending}
            onChange={(event) => update.mutate({ assigneeStaffEmail: event.target.value || null }, { onError })}
          >
            <option value="">Nobody</option>
            {members.map((member) => (
              <option key={member.email} value={member.email}>
                {member.name ?? member.email}
              </option>
            ))}
          </Select>
        </Fact>
        <Fact label="Priority (only Teamna sees it)">
          <Select
            label="Priority"
            value={ticket.priority}
            disabled={update.isPending}
            onChange={(event) => update.mutate({ priority: event.target.value as AdminSupportTicket["priority"] }, { onError })}
          >
            {supportPriorities.map((value) => (
              <option key={value} value={value}>
                {supportPriorityWords[value]}
              </option>
            ))}
          </Select>
        </Fact>
        <Fact label="Labels">
          <TicketTags ticket={ticket} tags={tags} />
        </Fact>
        <Fact label="Urgency (from the sender)">{supportUrgencyWords[ticket.urgency]}</Fact>
        <Fact label="Sender">
          <span dir="auto">{ticket.sender.name ?? "Unknown"}</span> ({ROLE_WORDS[ticket.senderRole]})
          {ticket.sender.phoneE164 ? <span className="block text-ink-2">{ticket.sender.phoneE164}</span> : null}
          {ticket.sender.email ? <span className="block text-ink-2">{ticket.sender.email}</span> : null}
        </Fact>
        <Fact label="Opened">{formatDateTime(ticket.createdAt)}</Fact>
        {ticket.rating ? <Fact label="Rating">{ticket.rating === "up" ? "Helped" : "Did not help"}</Fact> : null}
        {Object.entries(ticket.context).map(([key, value]) => (
          <Fact key={key} label={CONTEXT_WORDS[key] ?? key}>
            {value}
          </Fact>
        ))}
      </dl>
    </Card>
  );
}

function BusinessCard({ ticket }: { ticket: AdminSupportTicket }) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const card = ticket.businessCard;
  const actAs = () => {
    setActingAs({ businessId: card.id, businessName: card.name });
    // Owner data from a previous act-as must never paint for this business.
    queryClient.removeQueries({ predicate: (query) => query.queryKey[0] !== "cc" });
    void navigate({ to: "/owner/home" });
  };
  return (
    <Card className="px-4 py-3">
      <h2 className="text-[13px] font-semibold text-ink-2">Business</h2>
      <Link to="/businesses/$id" params={{ id: card.id }} className="mt-1 block text-[16px] font-semibold text-accent-strong" dir="auto">
        {card.name}
      </Link>
      <dl className="mt-1">
        <Fact label="Owner">
          <span dir="auto">{card.ownerName ?? "No owner"}</span>
          {card.ownerPhoneE164 ? <span className="block text-ink-2">{card.ownerPhoneE164}</span> : null}
          {card.ownerEmail ? <span className="block text-ink-2">{card.ownerEmail}</span> : null}
        </Fact>
        <Fact label="Teamna plan">{card.billingState ?? "No subscription"}</Fact>
        {card.demo ? <Fact label="Demo">A demo club. Not posted to Slack.</Fact> : null}
      </dl>
      <div className="mt-2">
        <Button variant="secondary" fullWidth onClick={actAs} disabled={!card.ownerName && !card.ownerPhoneE164 && !card.ownerEmail}>
          Open as this business
        </Button>
      </div>
    </Card>
  );
}
